import React from 'react';
import {
  VStack,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  HStack,
  Text,
  Tooltip,
  Badge,
  Input,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';



import {
  ClientType,
  ClientBudget,
  ClientComplexity,
  BusinessUnit,
} from '../../lib/pricing-tool-api/api';

export interface ClientTypeOption {
  choiceText: string;
  flavorText: string;
  value: ClientType;
}

export const CLIENT_TYPE_OPTIONS: ClientTypeOption[] = [
  {
    choiceText: 'Downballot',
    flavorText:
      'Any client focused on downballot races (ex: State Legislative races)',
    value: ClientType.Downballot,
  },
  {
    choiceText: 'House or Statewide Candidate',
    value: ClientType.HouseOrStatewide,
    flavorText: '',
  },
  {
    choiceText: 'National/DC PAC/IE/Advocacy Org',
    value: ClientType.NationalPacIeAdvocacyOrg,
    flavorText: '',
  },
  {
    choiceText: 'State/Local PAC/IE/Advocacy Org',
    value: ClientType.StateLocalPacIeAdvocacyOrg,
    flavorText: '',
  },
  {
    choiceText: 'Corporate',
    flavorText:
      'Corporate client, such as a tech company or a trade association, polling on non-electoral topics',
    value: ClientType.Corporate,
  },
];

function ClientInformationSection({
  clientBudget,
  clientType,
  clientComplexity,
  businessUnit,
  suggestedClientBudgets,
  suggestedClientComplexities,
  showClientBudgetReason,
  clientBudgetReason,
  setClientBudget,
  setClientType,
  setClientComplexity,
  setBusinessUnit,
  setClientBudgetReason,
}: {
  clientBudget: ClientBudget;
  clientType: ClientType;
  clientComplexity: ClientComplexity;
  businessUnit: BusinessUnit;
  suggestedClientBudgets: ClientBudget[];
  suggestedClientComplexities: ClientComplexity[];
  showClientBudgetReason: boolean;
  clientBudgetReason: string;
  setClientBudget: (v: ClientBudget) => void;
  setClientType: (v: ClientType) => void;
  setClientComplexity: (v: ClientComplexity) => void;
  setBusinessUnit: (v: BusinessUnit) => void;
  setClientBudgetReason: (v: string) => void;
}) {
  return (
    <VStack align="start" spacing="1rem">
      <FormControl>
        <FormLabel>Choose client type</FormLabel>
        <Text fontSize="sm" color="gray.600" mb={2} textAlign="left">
         Only affects suggested budget.
        </Text>
        <RadioGroup value={clientType} onChange={setClientType}>
          <VStack align="start">
            {CLIENT_TYPE_OPTIONS.map((clientTypeOption) => (
              <Radio key={clientTypeOption.value} value={clientTypeOption.value}>
                <Tooltip label={clientTypeOption.flavorText}>
                  <Text fontSize="sm">{clientTypeOption.choiceText}</Text>
                </Tooltip>
              </Radio>
            ))}
          </VStack>
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel>Business Unit</FormLabel>
        <RadioGroup value={businessUnit} onChange={setBusinessUnit}>
          <VStack align="start">
            <Radio value={BusinessUnit.Pna}>
              <Text fontSize="sm">PNA</Text>
            </Radio>
            <Radio value={BusinessUnit.Embold}>
              <Text fontSize="sm">Embold</Text>
            </Radio>
          </VStack>
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel>
          Choose client budget{' '}
          <Tooltip
            placement="right"
            hasArrow
            bg="white"
            color="gray.800"
            borderWidth="1px"
            borderColor="gray.200"
            maxWidth="400px"
            label={
              <>
                Budget recommendations based on client type & registered voters population:
                <br />
                <br /><b>Downballot:</b>
                <br />• {'<'} 300K voters: Low
                <br />• ≥ 300K voters: Medium
                <br />
                <br /><b>House/Statewide:</b> Medium
                <br />
                <br /><b>National PAC/IE/Advocacy:</b> Medium & High
                <br />
                <br /><b>State/Local PAC/IE/Advocacy:</b>
                <br />• {'>'} 4M voters: Medium & High
                <br />• ≤ 4M voters: Medium & Low
                <br />
                <br /><b>Corporate:</b> Medium & High
              </>
            }
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <RadioGroup value={clientBudget} onChange={setClientBudget}>
          <VStack align="start">
            {Object.entries(ClientBudget).map(([key, value]) => {
              const isRecommended = suggestedClientBudgets.includes(value);
              return (
                <HStack key={key} align="baseline">
                  <Radio value={value}>
                    <Text fontSize="sm">{key}</Text>
                  </Radio>
                  {isRecommended && (
                    <Tooltip
                      placement="top"
                      label="Recommended for client and district profile."
                    >
                      <Badge colorScheme="blue" variant="subtle" size="sm">
                        Recommended
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
              );
            })}
          </VStack>
        </RadioGroup>
      </FormControl>

      {showClientBudgetReason && (
        <FormControl>
          <FormLabel>
            Selected budget differs from the recommendation for this client type. A brief note on the rationale helps us improve the model over time.
          </FormLabel>
          <Input
            value={clientBudgetReason}
            onChange={(e) => setClientBudgetReason(e.target.value)}
          />
        </FormControl>
      )}

      <FormControl>
        <FormLabel>
          Choose client complexity{' '}
          <Tooltip
            placement="right"
            hasArrow
            bg="white"
            color="gray.800"
            borderWidth="1px"
            borderColor="gray.200"
            label={
              <>
                Examples of client complexity:
                <br />• Client has external partners
                <br />• Client has a large number of internal stakeholders
                <br />• Client lawyers are involved in contracting or drafting
                <br />• Final analysis will require significant back-and-forth with client
              </>
            }
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <Text fontSize="sm" color="gray.600" mb={2} textAlign="left">
          Additional client complexity relative to a standard engagement.
        </Text>
        <RadioGroup value={clientComplexity} onChange={setClientComplexity}>
          <VStack align="start">
            {Object.entries(ClientComplexity).map(([key, value]) => {
              const displayText = {
                'standard': 'Standard',
                'high': 'High (1-2 extra complexities)',
                'ultra': 'Ultra (3+ extra complexities)'
              }[value] || key;
              const isRecommended = suggestedClientComplexities.includes(value);

              return (
                <HStack key={key} align="baseline">
                  <Radio value={value}>
                    <Text fontSize="sm">{displayText}</Text>
                  </Radio>
                  {isRecommended && (
                    <Tooltip
                      placement="top"
                      label="Recommended for Embold business unit."
                    >
                      <Badge colorScheme="blue" variant="subtle" size="sm">
                        Recommended
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
              );
            })}
          </VStack>
        </RadioGroup>
      </FormControl>
    </VStack>
  );
}

export default ClientInformationSection;
